import wretch from 'wretch';
import { useQuery, queryOptions, UseQueryOptions } from '@tanstack/react-query';

import { useIsAuthenticated, useIsTokenExpired } from '../auth/user-info.hooks';
import { useRefreshAccessToken } from '../auth/useRefreshAccessToken';
import { useApiHeaders, ExtraHeaders } from './useApiHeaders';
import { useApiBase } from './useApiBase';

type ObjectMap = {
  [key: string]: any;
};

type ExtraQueryOptions = {
  isEncodedParams?: boolean;
  isLogoutApi?: boolean;
};

const apiWretcher = wretch(undefined).options({
  credentials: 'same-origin',
  mode: 'cors',
});

export function useApiQuery<TO = ObjectMap>(
  route: string,
  params?: ObjectMap,
  useQueryOptions?: Partial<UseQueryOptions<TO>>,
  extraHeaders?: ExtraHeaders,
  extraQueryOptions?: ExtraQueryOptions
) {
  const isAuthenticated = useIsAuthenticated();
  const isTokenExpired = useIsTokenExpired();
  const apiGet = useApiGet<TO>(extraQueryOptions);

  // Wait for authorization
  const enabled =
    ((!isTokenExpired && isAuthenticated) ||
      !!extraQueryOptions?.isLogoutApi) &&
    (useQueryOptions?.enabled || useQueryOptions?.enabled === undefined);

  const contentOptions = () => {
    return queryOptions<TO>({
      enabled,
      queryFn: () => apiGet(route, params, extraHeaders),
      queryKey: [route, params, extraHeaders],
      retry: 0,
    });
  };

  return useQuery<TO>({ ...useQueryOptions, ...contentOptions() });
}

export function useApiGet<TO = ObjectMap>(
  extraQueryOptions?: ExtraQueryOptions
) {
  const isLogoutQuery = !!extraQueryOptions?.isLogoutApi;
  const isEncodedParams =
    extraQueryOptions?.isEncodedParams &&
    localStorage.getItem('useUrlEncoding') === 'true';
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders(isLogoutQuery, isEncodedParams);
  const refreshAccessToken = useRefreshAccessToken();

  return async function (
    route: string,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const apiBase = getBaseUrl(route);
    const url = `${apiBase}${route}`;
    let wretcher = apiWretcher.url(url);
    if (params) {
      wretcher = wretcher.query(params);
    }
    const apiGetCall = () => {
      wretcher = wretcher.headers(getApiHeaders(extraHeaders));
      return wretcher.get().res((response) => jsonOrEmpty<TO>(response));
    };
    try {
      return await apiGetCall();
    } catch (error: any) {
      if (error?.response?.status === 401 && !isLogoutQuery) {
        const success = await refreshAccessToken();
        if (success) {
          return await apiGetCall();
        }
      }
      throw error;
    }
  };
}

export function useApiPost<TO = ObjectMap>(
  extraQueryOptions?: ExtraQueryOptions
) {
  const isLogoutQuery = !!extraQueryOptions?.isLogoutApi;
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders(isLogoutQuery);
  const refreshAccessToken = useRefreshAccessToken();

  return async function (
    route: string,
    body: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${getBaseUrl(route)}${route}`;
    let wretcher = apiWretcher.url(url);
    if (params) {
      wretcher = wretcher.query(params);
    }
    if (body) {
      wretcher = wretcher.body(JSON.stringify(body));
    }
    const apiPostCall = () => {
      wretcher = wretcher.headers(getApiHeaders(extraHeaders));
      return wretcher.post().res((response) => jsonOrEmpty<TO>(response));
    };
    try {
      return await apiPostCall();
    } catch (error: any) {
      if (error?.response?.status === 401 && !isLogoutQuery) {
        const success = await refreshAccessToken();
        if (success) {
          return await apiPostCall();
        }
      }
      throw error;
    }
  };
}

export function useApiPostFormData<TO = ObjectMap>(
  extraQueryOptions?: ExtraQueryOptions
) {
  const isLogoutQuery = !!extraQueryOptions?.isLogoutApi;
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders(isLogoutQuery);
  const refreshAccessToken = useRefreshAccessToken();

  return async function (
    route: string,
    formData: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${getBaseUrl(route)}${route}`;
    let wretcher = apiWretcher.url(url);
    if (params) {
      wretcher = wretcher.query(params);
    }
    if (formData) {
      wretcher = wretcher.formData(formData);
    }
    const apiPostCall = () => {
      const apiheaders = getApiHeaders(extraHeaders);
      //The browser has to infer the content-type automatically for multipart requests.
      delete apiheaders['content-type'];
      wretcher = wretcher.headers(apiheaders);
      return wretcher.post().res((response) => jsonOrEmpty<TO>(response));
    };
    try {
      return await apiPostCall();
    } catch (error: any) {
      if (error?.response?.status === 401 && !isLogoutQuery) {
        const success = await refreshAccessToken();
        if (success) {
          return await apiPostCall();
        }
      }
      throw error;
    }
  };
}

export function useApiPatch<TO = ObjectMap>() {
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders();
  const refreshAccessToken = useRefreshAccessToken();

  return async function (
    route: string,
    body: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${getBaseUrl(route)}${route}`;
    let wretcher = apiWretcher.url(url);
    if (params) {
      wretcher = wretcher.query(params);
    }
    if (body) {
      wretcher = wretcher.body(JSON.stringify(body));
    }
    const apiPatchCall = () => {
      wretcher = wretcher.headers(getApiHeaders(extraHeaders));
      return wretcher.patch().res((response) => jsonOrEmpty<TO>(response));
    };
    try {
      return await apiPatchCall();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        const success = await refreshAccessToken();
        if (success) {
          return await apiPatchCall();
        }
      }
      throw error;
    }
  };
}

export function useApiDelete<TO = ObjectMap>() {
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders();
  const refreshAccessToken = useRefreshAccessToken();

  return async function (
    route: string,
    body?: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${getBaseUrl(route)}${route}`;
    let wretcher = apiWretcher.url(url);
    if (params) {
      wretcher = wretcher.query(params);
    }
    if (body) {
      wretcher = wretcher.body(JSON.stringify(body));
    }
    const apiDeleteCall = () => {
      wretcher = wretcher.headers(getApiHeaders(extraHeaders));
      return wretcher.delete().res((response) => jsonOrEmpty<TO>(response));
    };
    try {
      return await apiDeleteCall();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        const success = await refreshAccessToken();
        if (success) {
          return await apiDeleteCall();
        }
      }
      throw error;
    }
  };
}

export function useApiPut<TO = ObjectMap>(
  extraQueryOptions?: ExtraQueryOptions
) {
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders();
  const refreshAccessToken = useRefreshAccessToken();

  return async function (
    route: string,
    body: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${getBaseUrl(route)}${route}`;
    let wretcher = apiWretcher.url(url);
    if (params) {
      wretcher = wretcher.query(params);
    }
    if (body) {
      wretcher = wretcher.body(JSON.stringify(body));
    }
    const apiPutCall = () => {
      wretcher = wretcher.headers(getApiHeaders(extraHeaders));
      return wretcher.put().res((response) => jsonOrEmpty<TO>(response));
    };
    try {
      return await apiPutCall();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        const success = await refreshAccessToken();
        if (success) {
          return await apiPutCall();
        }
      }
      throw error;
    }
  };
}

/**
 * WARNING: USE react-query `useMutation` for true POST apis that MUTATE data.
 *
 * This `useApiPostQuery` is only for apis that retrieve data and
 * DO NOT MUTATE data.
 * These apis should have been implemented with GET method
 * but currently use POST method instead.
 */
// eslint-disable-next-line max-params
export function useApiPostQuery<TO = ObjectMap>(
  route: string,
  body: ObjectMap,
  params?: ObjectMap,
  useQueryOptions?: Partial<UseQueryOptions<TO>>,
  extraHeaders?: ExtraHeaders,
  extraQueryOptions?: ExtraQueryOptions
) {
  const isAuthenticated = useIsAuthenticated();
  const apiPost = useApiPost<TO>(extraQueryOptions);

  // Wait for authorization
  const enabled =
    (isAuthenticated || !!extraQueryOptions?.isLogoutApi) &&
    (useQueryOptions?.enabled || useQueryOptions?.enabled === undefined);

  const contentOptions = () => {
    return queryOptions({
      enabled,
      queryFn: () => apiPost(route, body, params, extraHeaders),
      queryKey: [route, body, params, extraHeaders],
      retry: 0,
    });
  };

  return useQuery(contentOptions());
}

async function jsonOrEmpty<T = any>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return {} as T;
  }
}
